import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Layout from "./components/Layout";
import { Loading } from "./components/common";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import MyDashboard from "./pages/MyDashboard";
import Leads from "./pages/Leads";
import LeadDetail from "./pages/LeadDetail";
import FollowUps from "./pages/FollowUps";
import Pipeline from "./pages/Pipeline";
import Clients from "./pages/Clients";
import ClientDetail from "./pages/ClientDetail";
import Quotations from "./pages/Quotations";
import Projects from "./pages/Projects";
import ProjectDetail from "./pages/ProjectDetail";
import Tasks from "./pages/Tasks";
import Invoices from "./pages/Invoices";
import InvoiceDetail from "./pages/InvoiceDetail";
import Payments from "./pages/Payments";
import Expenses from "./pages/Expenses";
import Reports from "./pages/Reports";
import Performance from "./pages/Performance";
import Users from "./pages/Users";
import SettingsPage from "./pages/Settings";
import AuditLog from "./pages/AuditLog";
import Deadlines from "./pages/Deadlines";
import HR from "./pages/HR";
import LeadAgent from "./pages/LeadAgent";

const Denied = () => (
  <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="access-denied">
    <ShieldAlert className="h-12 w-12 text-rose-500 mb-3" />
    <h2 className="text-xl font-bold">Access denied</h2>
    <p className="text-sm text-muted-foreground mt-1">Your role does not have permission to view this page.</p>
  </div>
);

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading label="Checking session" />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function R({ roles, children }) {
  const { user } = useAuth();
  return roles.includes(user.role) ? children : <Denied />;
}

const Home = () => (useAuth().user.role === "owner" ? <Dashboard /> : <MyDashboard />);
const OS = ["owner", "sales"];
const O = ["owner"];
const OD = ["owner", "designer"];
const ALL = ["owner", "sales", "designer"];

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route element={<Protected><Layout /></Protected>}>
              <Route path="/" element={<Home />} />
              <Route path="/leads" element={<R roles={OS}><Leads /></R>} />
              <Route path="/leads/:id" element={<R roles={OS}><LeadDetail /></R>} />
              <Route path="/followups" element={<R roles={OS}><FollowUps /></R>} />
              <Route path="/pipeline" element={<R roles={OS}><Pipeline /></R>} />
              <Route path="/clients" element={<R roles={OS}><Clients /></R>} />
              <Route path="/clients/:id" element={<R roles={OS}><ClientDetail /></R>} />
              <Route path="/quotations" element={<R roles={OS}><Quotations /></R>} />
              <Route path="/projects" element={<R roles={ALL}><Projects /></R>} />
              <Route path="/projects/:id" element={<R roles={ALL}><ProjectDetail /></R>} />
              <Route path="/tasks" element={<R roles={OD}><Tasks /></R>} />
              <Route path="/deadlines" element={<R roles={ALL}><Deadlines /></R>} />
              <Route path="/lead-agent" element={<R roles={O}><LeadAgent /></R>} />
              <Route path="/hr" element={<R roles={O}><HR /></R>} />
              <Route path="/invoices" element={<R roles={O}><Invoices /></R>} />
              <Route path="/invoices/:id" element={<R roles={O}><InvoiceDetail /></R>} />
              <Route path="/payments" element={<R roles={O}><Payments /></R>} />
              <Route path="/expenses" element={<R roles={O}><Expenses /></R>} />
              <Route path="/reports" element={<R roles={O}><Reports /></R>} />
              <Route path="/performance" element={<R roles={OS}><Performance /></R>} />
              <Route path="/users" element={<R roles={O}><Users /></R>} />
              <Route path="/audit" element={<R roles={O}><AuditLog /></R>} />
              <Route path="/settings" element={<R roles={O}><SettingsPage /></R>} />
              <Route path="*" element={<Denied />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </div>
  );
}

export default App;
